import React, { useCallback, useMemo } from "react";
import { DollarsOnlyFormatter } from "@upserve/financials";
import { Maybe } from "true-myth";
import { prop } from "ramda";
import styled from "styles/styled_components";
import title from "styles/title";

import Icon, { ICON_COLOR, ICON_TYPE } from "components/shared/icon";
import LaborCostChart from "components/labor/labor_cost_chart";
import LaborOverviewBox from "components/labor/labor_overview_box";
import LaborTitle from "components/labor/labor_title";

import { trackEvent } from "helpers/analytics";
import { getLastYear } from "helpers/dates";
import useOnEscapeKey from "helpers/hooks/useOnEscapeKey";
import useToggle from "helpers/hooks/useToggle";
import { LaborCostsPerDay } from "helpers/insights/labor_costs";
import {
  getSalesForecastValuesForDay,
  getValuesOrEmpty,
  EMPTY_VAL,
  NO_DATA,
} from "helpers/insights/sales_forecast";
import { ISalesForecast } from "types/account";

interface IStyledExpanderProps {
  expanded?: boolean;
  hasForecast?: boolean;
}

const DRAWER_HEADER_HEIGHT = 50;
const DRAWER_HEIGHT = 450;
// Data table is not rendered when !hasForecast
const DATA_TABLE_HEIGHT = 40;

const StyledLaborDrawerSpace = styled("div")<IStyledExpanderProps>`
  height: ${(props) => (props.expanded ? "75vh" : "13em")};
`;

const StyledLaborDrawerWrapper = styled("div")<IStyledExpanderProps>`
  ${(props) => {
    let fullDrawerHeight = DRAWER_HEIGHT + DRAWER_HEADER_HEIGHT;
    if (!props.hasForecast) {
      fullDrawerHeight -= DATA_TABLE_HEIGHT;
    }
    return props.expanded
      ? `top: calc(100% - ${fullDrawerHeight}px);`
      : `top: calc(100% - ${DRAWER_HEADER_HEIGHT}px);`;
  }}
  left: 0;
  right: 0;
  position: fixed;
  transition: 0.3s;
  z-index: ${(props) => props.theme.layering.dropdown};
`;

const StyledLaborDrawerHeader = styled.div`
  background-color: ${(props) => props.theme.colors.uiDark};
  color: ${(props) => props.theme.colors.dsGrayLightest};
  line-height: ${(props) => props.theme.text.lineHeight.medium};
  padding: ${(props) =>
    `${(DRAWER_HEADER_HEIGHT - 24) / 2}px ${props.theme.spacing.small}`};
  width: 100%;
  ${title}

  svg {
    background-color: ${(props) => props.theme.colors.dsGrayLightest};
    border-radius: 50%;
    display: inline-block;
    // 24 lineHeight - 16px svg height
    margin: ${(24 - 16) / 2}px ${(props) => props.theme.spacing.small};
    padding: 1px;
    vertical-align: middle;
  }
`;

const StyledLaborDrawer = styled("div")<IStyledExpanderProps>`
  background-color: ${(props) => props.theme.colors.brandWhite};
  color: ${(props) => props.theme.colors.dsGrayDark};
  height: ${(props) =>
    props.hasForecast ? DRAWER_HEIGHT : DRAWER_HEIGHT - DATA_TABLE_HEIGHT}px;
  padding: ${(props) => props.theme.spacing.small};
  padding-top: ${(props) => props.theme.spacing.medium};
  transition: 0.3s;
  width: 100%;
  display: grid;
  grid-template-rows: 3fr 1fr;
  border-bottom: ${DRAWER_HEADER_HEIGHT}px solid
    ${(props) => props.theme.colors.uiDark};
  font-family: ${(props) => props.theme.text.primaryFamily};
`;

const StyledLaborMainContent = styled.div`
  display: grid;
  grid-template-columns: 150px auto;
`;

const StyledLaborOverview = styled.div`
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  padding: ${(props) => props.theme.spacing.xsmall};
`;

const StyledLaborSubHeader = styled.p`
  font-size: ${(props) => props.theme.text.size.base};
`;

const StyledLaborPercentage = styled.div`
  font-size: ${(props) => props.theme.text.size.largest};
  line-height: ${(props) => props.theme.text.lineHeight.large};
  font-family: ${(props) => props.theme.text.mediumFamily};
`;

const StyledLaborDollars = styled.div`
  font-size: ${(props) => props.theme.text.size.largest};
  line-height: ${(props) => props.theme.text.lineHeight.large};
  margin-bottom: ${(props) => props.theme.spacing.small};
`;

const StyledLaborChartWrapper = styled.div`
  display: grid;
  grid-template-rows: 35px auto;
`;

const StyledLaborChartTitle = styled.div`
  font-size: ${(props) => props.theme.text.size.large};
  justify-self: center;
  ${title}
  letter-spacing: 2px;
`;

const StyledLaborDrawerFooter = styled.div`
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: 220px repeat(7, 1fr);
  padding-right: ${(props) => props.theme.spacing.small};

  & div {
    justify-self: center;
  }
  & .label {
    font-family: ${(props) => props.theme.text.mediumFamily};
    justify-self: end;
  }
`;

const StyledLaborOverviewBoxWrapper = styled("div")<IStyledExpanderProps>`
  width: 190px;
  border: 1.5px solid ${(props) => props.theme.colors.uiDark};
  border-bottom: none;
  background: ${(props) => props.theme.colors.brandWhite};
  position: fixed;
  bottom: 50px;
  right: 45px;
  z-index: ${(props) => props.theme.layering.tooltip};
`;

interface ILaborDrawerProps {
  costsPerDay: LaborCostsPerDay;
  isDayView: boolean;
  salesForecast: Maybe<ISalesForecast>;
  totalCost: number;
  startDate: Maybe<Date>;
  endDate: Maybe<Date>;
}

const LABOR_COST_INFO = (
  <>
    <p>Labor costs are based on hourly employees.</p>
    <br />
    <p>For salaried employees, enter an hourly payrate in settings.</p>
  </>
);
const GROSS_SALES_INFO = `Your gross sales from this time last year (if available).`;

function LaborDrawer({
  costsPerDay,
  isDayView,
  salesForecast,
  totalCost,
  startDate,
  endDate,
}: ILaborDrawerProps) {
  const [expanded, toggleExpanded] = useToggle(false);
  const toggleWithAnalytics = useCallback(() => {
    const openClose = expanded ? "closed" : "opened";
    trackEvent(`Labor Health Insights ${openClose}`, {
      view: isDayView ? "day view" : "week view",
    });
    toggleExpanded();
  }, [expanded, isDayView, toggleExpanded]);
  const close = useCallback(() => {
    if (expanded) {
      toggleExpanded();
    }
  }, [expanded]);
  useOnEscapeKey(close);
  const iconType = expanded
    ? ICON_TYPE.MINUS_CIRCLE_SOLID
    : ICON_TYPE.PLUS_CIRCLE_SOLID;

  const [totalForecast, totalPercentage] = getValuesOrEmpty(
    totalCost,
    salesForecast.map(prop("totalGrossSales")).unwrapOr(0)
  );
  const hasActualForecast = totalPercentage !== EMPTY_VAL;
  const lastYear = getLastYear();

  // Build sales forecast and labor cost grid cells
  const [salesEls, laborCostEls] = useMemo(() => {
    const sales: JSX.Element[] = [];
    const laborCosts: JSX.Element[] = [];

    if (hasActualForecast) {
      let dayIndex = 0;
      costsPerDay.forEach((cost, dateString) => {
        const [salesThisDay, percentageThisDay] = getSalesForecastValuesForDay(
          salesForecast,
          cost.Total,
          dayIndex
        );
        sales.push(
          <div
            key={`sales${dayIndex}`}
            title={salesThisDay === EMPTY_VAL ? NO_DATA : ""}
          >
            {salesThisDay}
          </div>
        );
        laborCosts.push(
          <div
            key={dateString}
            title={percentageThisDay === EMPTY_VAL ? NO_DATA : ""}
          >
            {percentageThisDay}
          </div>
        );
        dayIndex++;
      });
    }
    return [sales, laborCosts];
  }, [hasActualForecast, costsPerDay, salesForecast]);

  return (
    <>
      <StyledLaborOverviewBoxWrapper expanded={expanded}>
        <LaborOverviewBox
          totalCost={DollarsOnlyFormatter.format(totalCost)}
          totalPercent={totalPercentage}
          startDate={startDate}
          endDate={endDate}
        />
      </StyledLaborOverviewBoxWrapper>
      <StyledLaborDrawerSpace expanded={expanded} />
      <StyledLaborDrawerWrapper
        expanded={expanded}
        hasForecast={hasActualForecast}
      >
        <StyledLaborDrawerHeader onClick={toggleWithAnalytics}>
          <Icon size="16px" type={iconType} color={ICON_COLOR.BLUE} />
          Labor Health Insights
        </StyledLaborDrawerHeader>
        <StyledLaborDrawer expanded={expanded} hasForecast={hasActualForecast}>
          <StyledLaborMainContent>
            <StyledLaborOverview>
              <div>
                <LaborTitle align="left" info={LABOR_COST_INFO}>
                  Labor Cost
                </LaborTitle>
                <StyledLaborSubHeader>Scheduled</StyledLaborSubHeader>
                <StyledLaborDollars>
                  {DollarsOnlyFormatter.format(totalCost)}
                </StyledLaborDollars>
                {hasActualForecast && (
                  <>
                    <StyledLaborSubHeader>Labor Cost (%)</StyledLaborSubHeader>
                    <StyledLaborPercentage
                      title={totalPercentage === EMPTY_VAL ? NO_DATA : ""}
                    >
                      {totalPercentage}
                    </StyledLaborPercentage>
                  </>
                )}
              </div>
              {hasActualForecast && (
                <div>
                  <LaborTitle align="left" info={GROSS_SALES_INFO}>
                    {lastYear} Gross Sales
                  </LaborTitle>
                  <StyledLaborDollars
                    title={totalForecast === EMPTY_VAL ? NO_DATA : ""}
                  >
                    {totalForecast}
                  </StyledLaborDollars>
                </div>
              )}
            </StyledLaborOverview>
            <StyledLaborChartWrapper>
              <StyledLaborChartTitle>
                Scheduled Labor Costs by day
              </StyledLaborChartTitle>
              <LaborCostChart
                costsPerDay={costsPerDay}
                salesForecast={
                  hasActualForecast ? salesForecast : Maybe.nothing()
                }
              />
            </StyledLaborChartWrapper>
          </StyledLaborMainContent>
          {hasActualForecast && (
            <StyledLaborDrawerFooter>
              <div className="label">{lastYear} Gross Sales:</div>
              {salesEls}
              <div className="label">Labor Cost (%):</div>
              {laborCostEls}
            </StyledLaborDrawerFooter>
          )}
        </StyledLaborDrawer>
      </StyledLaborDrawerWrapper>
    </>
  );
}

export default LaborDrawer;
